import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "../components/ui/dialog";
import { Heart, Target, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const NAVY = "#0A2463";
const RED = "#C8102E";

export default function Donations() {
    const { user } = useAuth();
    const [causes, setCauses] = useState([]);

    const load = () => api.get("/causes?active_only=true").then(({ data }) => setCauses(data));
    useEffect(() => { load().catch(() => {}); }, []);

    return (
        <div className="bg-slate-50 min-h-screen">
            <section className="bg-white border-b-2" style={{ borderColor: RED }}>
                <div className="max-w-6xl mx-auto px-6 lg:px-10 py-12">
                    <div className="text-xs uppercase tracking-[0.25em] font-bold mb-2" style={{ color: RED }}>Support the mission</div>
                    <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter" style={{ color: NAVY }}>Donations & Causes</h1>
                    <p className="text-slate-600 mt-3 max-w-2xl">Every contribution funds a brother, a sister, a family, or a future Trendsetter.</p>
                </div>
            </section>

            <section className="max-w-6xl mx-auto px-6 lg:px-10 py-10">
                {causes.length === 0 ? (
                    <div className="text-center py-16 text-slate-500">
                        <Heart className="h-12 w-12 mx-auto text-slate-300" />
                        <p className="mt-3">No active causes right now. Check back soon.</p>
                    </div>
                ) : (
                    <div className="grid lg:grid-cols-2 gap-6">
                        {causes.map((c) => <CauseCard key={c.id} cause={c} canPledge={!!user} onPledged={load} />)}
                    </div>
                )}
            </section>
        </div>
    );
}

function CauseCard({ cause, canPledge, onPledged }) {
    const [confirmOpen, setConfirmOpen] = useState(false);
    const pct = cause.goal_amount > 0 ? Math.min(100, Math.round((cause.raised_amount / cause.goal_amount) * 100)) : 0;

    function startDonate() {
        // Open Zeffy in a new tab AND surface the confirmation dialog so
        // the member can paste the receipt when they come back and get
        // credited immediately.
        if (cause.zeffy_url) {
            window.open(cause.zeffy_url, "_blank", "noopener,noreferrer");
        }
        setConfirmOpen(true);
    }

    return (
        <div className="bg-white rounded-2xl overflow-hidden border-2 border-slate-200 shadow-warm flex flex-col" data-testid={`cause-${cause.id}`}>
            <div className="aspect-[2/1] bg-slate-100 overflow-hidden">
                {cause.cover_image && <img src={cause.cover_image} alt={cause.title} className="w-full h-full object-contain" />}
            </div>
            <div className="p-6 flex-1 flex flex-col">
                <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: RED }}>{cause.category}</div>
                <h3 className="font-heading font-black text-xl mt-1 leading-tight" style={{ color: NAVY }}>{cause.title}</h3>
                <p className="text-sm text-slate-600 mt-3 leading-relaxed">{cause.description}</p>

                <div className="mt-5 flex items-end justify-between gap-3">
                    <div>
                        <div className="font-heading font-black text-2xl" style={{ color: NAVY }}>${cause.raised_amount.toFixed(0)}</div>
                        <div className="text-xs text-slate-500"><Target className="h-3 w-3 inline" /> of ${cause.goal_amount.toFixed(0)} goal</div>
                    </div>
                    <div className="text-right">
                        <div className="font-heading font-black text-2xl" style={{ color: RED }}>{pct}%</div>
                        <div className="text-xs text-slate-500">{cause.donor_count} donor{cause.donor_count !== 1 ? "s" : ""}</div>
                    </div>
                </div>
                <div className="mt-3 w-full bg-slate-100 rounded-full h-2 overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${NAVY}, ${RED})` }} />
                </div>

                <div className="mt-6 flex flex-col gap-2">
                    {canPledge ? (
                        cause.zeffy_url ? (
                            <>
                                <Button
                                    onClick={startDonate}
                                    className="w-full rounded-full text-white shadow-warm hover:opacity-90"
                                    style={{ backgroundColor: RED }}
                                    data-testid={`zeffy-donate-${cause.id}`}
                                >
                                    <Heart className="h-4 w-4 mr-1.5" /> Donate via Zeffy
                                </Button>
                                <button
                                    type="button"
                                    onClick={() => setConfirmOpen(true)}
                                    className="text-xs text-slate-500 hover:text-primary underline underline-offset-2 self-center"
                                    data-testid={`already-donated-${cause.id}`}
                                >
                                    Already donated? Get credit →
                                </button>
                            </>
                        ) : (
                            // Donations through PayPal in-app checkout were retired.
                            // Until an officer adds a Zeffy URL to this cause, members
                            // see a friendly hand-off message rather than a dead button.
                            <div className="w-full rounded-2xl bg-slate-50 border border-slate-200 px-4 py-3 text-sm text-slate-600" data-testid={`donate-unavailable-${cause.id}`}>
                                Online giving for this cause is being set up. Contact your chapter officers to donate directly in the meantime.
                            </div>
                        )
                    ) : (
                        <Button asChild className="w-full rounded-full text-white shadow-warm" style={{ backgroundColor: RED }}>
                            <a href="/login">Log in to donate</a>
                        </Button>
                    )}
                </div>
            </div>
            {confirmOpen && (
                <ZeffyConfirmDialog
                    cause={cause}
                    onClose={() => setConfirmOpen(false)}
                    onSaved={() => { setConfirmOpen(false); onPledged?.(); }}
                />
            )}
        </div>
    );
}

/**
 * Post-donate confirmation form. The member pastes their Zeffy receipt
 * number (or ZF-ref, or the confirmation email address Zeffy sent them)
 * plus the amount they donated. Backend creates a `donation` transaction
 * — auto-approved for trusted members, otherwise pending admin review.
 */
function ZeffyConfirmDialog({ cause, onClose, onSaved }) {
    const [amount, setAmount] = useState("");
    const [confirmation, setConfirmation] = useState("");
    const [anonymous, setAnonymous] = useState(false);
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);

    async function submit() {
        const amt = parseFloat(amount);
        if (Number.isNaN(amt) || amt <= 0) { toast.error("Enter the donation amount"); return; }
        if (!confirmation.trim()) { toast.error("Paste your Zeffy receipt or confirmation email"); return; }
        setBusy(true);
        try {
            const { data } = await api.post("/donations/zeffy/confirm", {
                cause_id: cause.id,
                amount: amt,
                confirmation: confirmation.trim(),
                anonymous,
                note: note.trim(),
            });
            toast.success(data.message || "Donation recorded — thanks!");
            onSaved?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not record your donation");
        } finally {
            setBusy(false);
        }
    }

    return (
        <Dialog open onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-md" data-testid="zeffy-confirm-dialog">
                <DialogHeader>
                    <DialogTitle className="font-heading text-2xl" style={{ color: NAVY }}>Confirm your donation</DialogTitle>
                    <DialogDescription>
                        We opened Zeffy in a new tab. After you finish, come back and paste your Zeffy receipt below so we can credit you.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3 mt-2">
                    <div className="rounded-xl bg-slate-50 border border-slate-200 px-3 py-2 text-xs text-slate-600 flex items-center gap-2">
                        <ExternalLink className="h-3.5 w-3.5 shrink-0" />
                        Donating to <b className="ml-1 truncate">{cause.title}</b>
                    </div>
                    <div>
                        <Label>Amount donated (USD) *</Label>
                        <Input
                            type="number"
                            step="0.01"
                            min="0.01"
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                            placeholder="25.00"
                            className="rounded-xl mt-1.5"
                            data-testid="zeffy-confirm-amount"
                        />
                    </div>
                    <div>
                        <Label>Zeffy receipt or confirmation *</Label>
                        <Input
                            value={confirmation}
                            onChange={(e) => setConfirmation(e.target.value)}
                            placeholder="RCT-0401-5406, ZF-ABC123, or your confirmation email"
                            className="rounded-xl mt-1.5"
                            data-testid="zeffy-confirm-receipt"
                        />
                        <p className="text-[11px] text-slate-500 mt-1">
                            Zeffy emails you a receipt right after checkout — paste the receipt number or the email address it was sent to.
                        </p>
                    </div>
                    <div>
                        <Label className="flex items-center gap-2 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={anonymous}
                                onChange={(e) => setAnonymous(e.target.checked)}
                                data-testid="zeffy-confirm-anonymous"
                            />
                            <span className="text-sm">Show as anonymous on the donor leaderboard</span>
                        </Label>
                    </div>
                    <div>
                        <Label>Note (optional)</Label>
                        <Textarea
                            rows={2}
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            placeholder="In memory of…, tribute, chapter, etc."
                            className="rounded-xl mt-1.5"
                            data-testid="zeffy-confirm-note"
                        />
                    </div>
                </div>
                <DialogFooter className="mt-4">
                    <Button variant="outline" onClick={onClose} disabled={busy} className="rounded-full">Cancel</Button>
                    <Button
                        onClick={submit}
                        disabled={busy}
                        className="rounded-full text-white hover:opacity-90"
                        style={{ backgroundColor: RED }}
                        data-testid="zeffy-confirm-submit"
                    >
                        {busy ? "Submitting…" : "Submit for credit"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
